import { BrowserRouter, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AppShell } from "@/components/AppShell";
import { OfflineProvider } from "@/offline/OfflineProvider";
import { AuthProvider } from "@/services/supabase/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AccessDeniedPage from "@/pages/AccessDeniedPage";
import DashboardPage from "@/pages/DashboardPage";
import FarmersPage from "@/pages/FarmersPage";
import AddFarmerPage from "@/pages/AddFarmerPage";
import FarmerProfilePage from "@/pages/FarmerProfilePage";
import FarmerEditPage from "@/pages/FarmerEditPage";
import FarmerGroupsPage from "@/pages/FarmerGroupsPage";
import FarmsPage from "@/pages/FarmsPage";
import FarmProfilePage from "@/pages/FarmProfilePage";
import FarmFieldUpdatePage from "@/pages/FarmFieldUpdatePage";
import PlotFieldUpdatePage from "@/pages/PlotFieldUpdatePage";
import VisitsPage from "@/pages/VisitsPage";
import VisitFormPage from "@/pages/VisitFormPage";
import VisitPlanFormPage from "@/pages/VisitPlanFormPage";
import VisitPlanDetailPage from "@/pages/VisitPlanDetailPage";
import VisitStartPage from "@/pages/VisitStartPage";
import VisitExecutionPage from "@/pages/VisitExecutionPage";
import UnscheduledVisitStartPage from "@/pages/UnscheduledVisitStartPage";
import VisitDetailPage from "@/pages/VisitDetailPage";
import InputsPage from "@/pages/InputsPage";
import NurseryManagementPage from "@/pages/NurseryManagementPage";
import NurseryLotDetailPage from "@/pages/NurseryLotDetailPage";
import InventoryPage from "@/pages/InventoryPage";
import DistributionsPage from "@/pages/DistributionsPage";
import SeedlingDistributionPlanFormPage from "@/pages/SeedlingDistributionPlanFormPage";
import SeedlingDistributionPlanDetailPage from "@/pages/SeedlingDistributionPlanDetailPage";
import HarvestPage from "@/pages/HarvestPage";
import TransactionsPage from "@/pages/TransactionsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import StaffPage from "@/pages/StaffPage";
import StaffFarmerAllocationPage from "@/pages/StaffFarmerAllocationPage";
import StaffProfilePage from "@/pages/StaffProfilePage";
import AddStaffPage from "@/pages/AddStaffPage";
import EditStaffPage from "@/pages/EditStaffPage";
import AssetsPage from "@/pages/AssetsPage";
import FinancePage from "@/pages/FinancePage";
import AccountsPage from "@/pages/AccountsPage";
import CommercialPage from "@/pages/CommercialPage";
import AgreementsPage from "@/pages/AgreementsPage";
import ReportsPage from "@/pages/ReportsPage";
import ModuleFoundationPage from "@/pages/ModuleFoundationPage";
import ConflictReviewPage from "@/pages/ConflictReviewPage";
import "@/App.css";

/**
 * Detects Supabase recovery tokens in the URL hash and redirects to
 * /reset-password so the ResetPasswordPage can handle the PASSWORD_RECOVERY
 * flow. Supabase may redirect to the Site URL root (e.g. http://localhost:3000/)
 * with the token in the hash — this component ensures the user lands on the
 * reset-password page regardless of the Site URL configuration.
 */
function RecoveryRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    // Supabase recovery links contain type=recovery (or type=signup) in the hash
    if (hash && (hash.includes("type=recovery") || hash.includes("type=signup"))) {
      if (location.pathname !== "/reset-password") {
        navigate("/reset-password", { replace: true });
      }
    }
  }, [navigate, location.pathname]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RecoveryRedirect />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<OfflineProvider><AppShell /></OfflineProvider>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/farmers" element={<FarmersPage />} />
              <Route path="/farmers/new" element={<AddFarmerPage />} />
              <Route path="/farmers/:farmerId" element={<FarmerProfilePage />} />
              <Route path="/farmers/:farmerId/edit" element={<FarmerEditPage />} />
              <Route path="/farmer-groups" element={<FarmerGroupsPage />} />
              <Route path="/farms" element={<FarmsPage />} />
              <Route path="/farms/:farmId" element={<FarmProfilePage />} />
              <Route path="/farms/:farmId/edit" element={<FarmFieldUpdatePage />} />
              <Route path="/farms/:farmId/plots/:plotId/map" element={<PlotFieldUpdatePage />} />
              <Route path="/visits" element={<VisitsPage />} />
              <Route path="/visits/new" element={<VisitFormPage />} />
              <Route path="/visits/plans/new" element={<VisitPlanFormPage />} />
              <Route path="/visits/plans/:planId" element={<VisitPlanDetailPage />} />
              <Route path="/visits/plans/:planId/targets/:targetId/start" element={<VisitStartPage />} />
              <Route path="/visits/unscheduled/start" element={<UnscheduledVisitStartPage />} />
              <Route path="/visits/:visitId/execute" element={<VisitExecutionPage />} />
              <Route path="/visits/:visitId" element={<VisitDetailPage />} />
              <Route path="/inputs" element={<InputsPage />} />
              <Route path="/nursery" element={<NurseryManagementPage />} />
              <Route path="/nursery/lots/:lotId" element={<NurseryLotDetailPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/distributions" element={<DistributionsPage />} />
              <Route path="/distributions/seedlings/plans/new" element={<SeedlingDistributionPlanFormPage />} />
              <Route path="/distributions/seedlings/plans/:planId" element={<SeedlingDistributionPlanDetailPage />} />
              <Route path="/harvest" element={<HarvestPage />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/staff/farmer-allocations" element={<StaffFarmerAllocationPage />} />
              <Route path="/staff/new" element={<AddStaffPage />} />
              <Route path="/staff/:staffId" element={<StaffProfilePage />} />
              <Route path="/staff/:staffId/edit" element={<EditStaffPage />} />
              <Route path="/conflicts" element={<ConflictReviewPage />} />
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/accounts" element={<AccountsPage />} />
              <Route path="/finance" element={<FinancePage />} />
              <Route path="/commercial" element={<CommercialPage />} />
              <Route path="/agreements" element={<AgreementsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/:moduleSlug" element={<ModuleFoundationPage />} />
            </Route>
          </Route>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster position="top-right" richColors={false} closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
